import { ChatEntity } from "@/indexedDB/types";

import { ContextMenu, ContextMenuTrigger } from "@primitives/context-menu";
import { useTranslation } from "react-i18next";
import { resetChatUnreadMessages } from "@/middleware/counters";

import RingPulse from "@/components/shared/RingPulse";
import { useCallState } from "@/zustand/CallState";

import {
  ChatUserAction,
  isMiddlePaneAction,
  useNavigationState,
} from "@/zustand/NavigationState";

import { cn, formatChatListEntryTimestamp } from "@lib/utils";
import { BellOff, Pin, Rss, Users } from "lucide-react";
import { Badge } from "@primitives/badge";

import { ChatType } from "@/server/types/chat";
import { CHAT_HUB_ID } from "@/config/constants";
import ChatAvatar from "@/components/shared/ChatAvatar";

import useChatLastMessageParser from "@/hooks/useChatLastMessageParser";
import { useTypingLabel } from "@/components/chat/typing/useTypingLabel";
import TypingDots from "@/components/chat/typing/TypingDots";

import ChatListEntryMenu from "./ChatListEntryMenu";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarImage } from "@/components/primitives/avatar";

/** Estimated row height for virtualizer. Matches p-2 + size-10 avatar + 2 text rows. */
export const CHAT_LIST_ITEM_HEIGHT = 64;

export default function ChatListEntry({ chat }: { chat: ChatEntity }) {
  const navigate = useNavigate();
  const activeChatId = useNavigationState((state) => state.activeChatId);
  const activeChatUserAction = useNavigationState(
    (state) => state.activeChatUserAction,
  );
  const setActiveChatId = useNavigationState((state) => state.setActiveChatId);

  const callInfo = useCallState((state) => state.callInfo);
  const activeCallIds = useCallState((state) => state.activeCallIds);
  const { t } = useTranslation();

  const isChatWithArchitect =
    chat.type === ChatType.GROUP && chat.hubId === CHAT_HUB_ID;

  const showPulse =
    activeCallIds.includes(chat.id || "") ||
    chat.id === callInfo?.currentCallId;
  const callMessage = t("videoCall.inProgress");

  const lastMessageText = useChatLastMessageParser({
    message: chat.lastMessage,
    chatType: chat.type,
    description: chat.description,
  });

  // While someone is typing in this chat, the typing label replaces the
  // last-message preview (same store/resolver as the message view, so OA chats
  // show "Responding..." here too).
  const typingLabel = useTypingLabel(chat.id, chat.type);

  return (
    <ContextMenu>
      <ContextMenuTrigger
        onClick={() => {
          // When an overlay action (payments pane, settings category view, new
          // AI chat) is open and the user clicks the chat that's already in
          // the URL, navigate() is a no-op and useChatRouteSync never fires.
          // Directly calling setActiveChatId clears the action and shows the
          // chat regardless.
          if (
            isMiddlePaneAction(activeChatUserAction) ||
            activeChatUserAction === ChatUserAction.CREATE_AI_CHAT
          ) {
            void setActiveChatId(chat.id);
          }
          navigate(`/${chat.id}`);
          resetChatUnreadMessages({ chatId: chat.id });
        }}
        className={cn(
          "relative flex w-full cursor-pointer select-none items-center gap-2 overflow-hidden rounded-md p-2 lg:hover:bg-primary/80 lg:hover:text-primary-foreground",
          chat.id === activeChatId && "bg-primary text-primary-foreground",
          "data-[state=open]:bg-primary data-[state=open]:text-primary-foreground",
        )}
      >
        {/* AVATAR */}

        <div className="relative size-10">
          {isChatWithArchitect && !chat.avatar?.attachmentId ? (
            <>
              <Avatar
                aria-label={t("aria.chatAvatar")}
                className="flex size-10 items-center justify-center bg-transparent align-middle text-sm"
              >
                <AvatarImage
                  className="size-6 invert dark:invert-0"
                  src="/icons/logo-architect.svg"
                />
              </Avatar>
            </>
          ) : (
            <>
              <ChatAvatar
                chatId={chat.id}
                chatName={chat.name}
                attachmentId={chat.avatar?.attachmentId}
                secondMemberId={chat.avatar?.secondMemberId}
                isMuted={chat.isMuted}
              />
              <RingPulse active={showPulse} className="bg-secondary" />
            </>
          )}
        </div>

        <div className="flex flex-1 flex-col overflow-hidden">
          {/* CHAT TITLE & TIMESTATMP */}
          <div className="line-clamp-1 flex items-center gap-2 overflow-hidden">
            {chat.isMuted && isChatWithArchitect && (
              <BellOff size={14} className="shrink-0 text-secondary" />
            )}

            {chat.isPinned && (
              <Pin size={14} className="shrink-0 text-secondary" />
            )}

            {!isChatWithArchitect && (
              <>
                {chat.type === ChatType.GROUP &&
                  (chat.isPublic ? (
                    <Rss size={14} className="shrink-0" />
                  ) : (
                    <Users size={14} className="shrink-0" />
                  ))}
              </>
            )}

            <h4 className="text-md truncate font-semibold leading-tight">
              {chat.name}
            </h4>

            <div className="ml-auto whitespace-nowrap text-xs text-muted-2">
              {chat.lastMessageTimestamp &&
                formatChatListEntryTimestamp(chat.lastMessageTimestamp)}
            </div>
          </div>
          {/* CHAT LAST MSG & UNREAD COUNTER */}
          <div className="flex w-full items-center gap-2 overflow-hidden">
            {!showPulse && typingLabel ? (
              <h5 className="flex min-w-0 items-center gap-1.5 truncate text-sm font-normal leading-tight text-muted-2">
                <span className="truncate">{typingLabel}</span>
                <TypingDots />
              </h5>
            ) : (
              <h5 className="truncate text-sm font-normal leading-tight text-muted-2">
                {showPulse ? callMessage : lastMessageText}
              </h5>
            )}
            {chat.unreadCount > 0 && (
              <Badge
                variant="default"
                className={cn(
                  "ml-auto h-3 w-6 shrink-0 items-center justify-center rounded-lg bg-primary p-2 align-middle text-xs text-primary-foreground",
                  chat.id === activeChatId &&
                    "bg-secondary text-secondary-foreground",
                )}
              >
                {chat.unreadCount > 9 ? "9+" : chat.unreadCount}
              </Badge>
            )}
          </div>
        </div>
      </ContextMenuTrigger>

      <ChatListEntryMenu chat={chat} />
    </ContextMenu>
  );
}
